import React, { useState, useEffect } from "react";
import axiosUserInstance from "../../axiosUserInstance";
import { toast } from "react-toastify";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const PhoneVerificationModal = ({ onVerified, onClose }) => {
  const [step, setStep] = useState(1); // 1 = phone input, 2 = OTP input
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((value) => Math.max(value - 1, 0)), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const sendOtp = async (phoneNumber) => {
    setPhoneError("");
    setInfoMessage("");
    if (!phoneNumber || phoneNumber.trim().length < 5) {
      setPhoneError("Please enter a valid phone number");
      return false;
    }

    setIsLoading(true);
    try {
      const res = await axiosUserInstance.post("/referral/phone/send-otp", {
        phone: phoneNumber.trim(),
      });
      if (res.status === 200) {
        toast.success("OTP sent! Check your phone.");
        setResendTimer(30);
        setInfoMessage("If you did not receive it, you can resend after the timer.");
        return true;
      }
    } catch (err) {
      if (err?.response?.status === 400 && err?.response?.data?.message) {
        setPhoneError(err.response.data.message);
      } else {
        toast.error(err?.response?.data?.message || "Failed to send OTP");
      }
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  const handleSendOtp = async () => {
    const success = await sendOtp(phone);
    if (success) {
      setStep(2);
      setOtp("");
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) {
      return;
    }
    const success = await sendOtp(phone);
    if (success) {
      setOtp("");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.trim().length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axiosUserInstance.post("/referral/phone/verify-otp", {
        phone: phone.trim(),
        otp: otp.trim(),
      });
      if (res.status === 200 && res.data.verified) {
        toast.success("Phone verified! You can now enter your referral code.");
        onVerified(phone.trim());
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="phone-modal-overlay" onClick={onClose}>
      <div
        className="phone-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="phone-modal-header">
          <div className="phone-modal-icon">📱</div>
          <h2>Verify Your Phone</h2>
          <p>We need to verify your phone before applying a referral code</p>
          <button className="phone-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="phone-modal-body">
          {/* Step indicators */}
          <div className="phone-modal-steps">
            <div className={`step-dot ${step >= 1 ? "active" : ""}`}>1</div>
            <div className={`step-line ${step >= 2 ? "active" : ""}`}></div>
            <div className={`step-dot ${step >= 2 ? "active" : ""}`}>2</div>
          </div>

          {step === 1 && (
            <div className="phone-modal-step">
              <label>Phone Number</label>
              <div className="custom-phone-input-wrapper" style={{ width: '100%' }}>
                <PhoneInput
                  country={'in'}
                  value={phone}
                  onChange={phone => {
                    setPhone(phone);
                    if (phoneError) setPhoneError("");
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '54px',
                    fontSize: '1rem',
                    borderRadius: '10px',
                    border: '2px solid #e0e0e0',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                  }}
                  buttonStyle={{
                    border: '2px solid #e0e0e0',
                    borderRight: '1px solid #e0e0e0',
                    borderRadius: '10px 0 0 10px',
                    backgroundColor: 'transparent'
                  }}
                  dropdownStyle={{
                    width: '300px'
                  }}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    autoFocus: true
                  }}
                />
              </div>
              {phoneError ? (
                <p className="error-message" style={{ marginTop: '4px', marginBottom: '8px', fontSize: '0.85rem' }}>
                  {phoneError}
                </p>
              ) : (
                <p className="phone-modal-hint" style={{ marginTop: '4px', marginBottom: '8px' }}>
                  We will send an OTP via SMS
                </p>
              )}
              <button
                className="btn btn-primary phone-modal-btn"
                onClick={handleSendOtp}
                disabled={isLoading}
              >
                {isLoading ? "Sending…" : "Send OTP"}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="phone-modal-step">
              <label>Enter OTP sent to +{phone}</label>
              <input
                type="text"
                className="form-control otp-input"
                placeholder="● ● ● ● ● ●"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                autoFocus
              />
              <button
                className="btn btn-primary phone-modal-btn"
                onClick={handleVerifyOtp}
                disabled={isLoading}
              >
                {isLoading ? "Verifying…" : "Verify OTP"}
              </button>
              <div className="phone-modal-resend-group">
                <button
                  className="phone-modal-resend"
                  onClick={() => setStep(1)}
                  type="button"
                >
                  ← Change number
                </button>
                <button
                  className="phone-modal-resend"
                  onClick={handleResendOtp}
                  type="button"
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0
                    ? `Resend OTP in ${resendTimer}s`
                    : "Resend OTP"}
                </button>
              </div>
              {infoMessage && (
                <p className="phone-modal-hint" style={{ marginTop: '8px' }}>
                  {infoMessage}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
